import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { servicesAPI } from '../services/api';
import { Service } from '../types';
import { CheckCircle, Clock, DollarSign, ArrowRight, Sparkles } from 'lucide-react';
import { FadeIn, Stagger, StaggerItem, ScaleIn } from '../components/animations';
import { Card, CardBody } from '../components/ui';

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getAll({ isActive: true });
      setServices(response.data.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">Professional Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional career services designed to help you succeed
            </p>
          </div>
        </FadeIn>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        ) : services.length === 0 ? (
          <FadeIn delay={0.2}>
            <Card className="text-center py-16">
              <CardBody>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No services available</h3>
                <p className="text-gray-600">Check back soon for new services</p>
              </CardBody>
            </Card>
          </FadeIn>
        ) : (
          <Stagger>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {services.map((service, index) => (
                <StaggerItem key={service._id}>
                  <Link to={`/services/${service.slug || service._id}`}>
                    <Card hover className="h-full group">
                      <CardBody className="p-8">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                          <DollarSign className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.name}</h3>
                        
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-4xl font-bold text-blue-600">${service.price}</span>
                          <span className="text-gray-500">per service</span>
                        </div>
                        
                        <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
                          <Clock className="w-4 h-4" />
                          <span>Delivery: {service.deliveryTime}</span>
                        </div>

                        <div className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition group-hover:shadow-lg">
                          View Details
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardBody>
                    </Card>
                  </Link>
                </StaggerItem>
              ))}
            </div>
          </Stagger>
        )}

        <ScaleIn delay={0.4}>
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Help with Your Career?</h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Our expert consultants are here to help you succeed
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <button className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition shadow-lg">
                    Get Started Free
                  </button>
                </Link>
                <Link to="/about">
                  <button className="px-8 py-4 bg-blue-500/20 backdrop-blur-sm text-white rounded-lg font-semibold border-2 border-white/30 hover:bg-blue-500/30 transition">
                    Learn More
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </ScaleIn>

        <FadeIn delay={0.6}>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <CheckCircle className="w-6 h-6" />, title: 'Expert Guidance', desc: 'Get advice from industry professionals' },
              { icon: <Clock className="w-6 h-6" />, title: 'Fast Delivery', desc: 'Quick turnaround on all services' },
              { icon: <Sparkles className="w-6 h-6" />, title: 'Quality Assured', desc: 'Satisfaction guaranteed or money back' }
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 text-blue-600">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default Services;
